/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { PlanName, WxoPlanName } from '../../../../ts/plan.ts';
import {
  CloudEnvironment,
  PlatformTitle,
  ProductCode,
  ProductCodeType,
  ProductId,
  ProductTitle,
  Ut30Code
} from '../../../../ts/segment';
import { SkillType } from '../../../../ts/skill';
import { channelTypeToChannelName } from '../../../channels/util/channelTypes';
import { deployEnv, isAws, isWxoEnabled } from '../../util/config';

export const CUSTOM_ENVIRONMENT_ID = 'custom.environmentId';
export const CUSTOM_WA_ENVIRONMENT_ID = 'custom.waEnvironmentId';
export const CUSTOM_TARGET_ENVIRONMENT_ID = 'custom.targetEnvironmentId';
export const CUSTOM_WA_TARGET_ENVIRONMENT_ID = 'custom.waTargetEnvironmentId';
export const CUSTOM_EXTENSION_ID = 'custom.customExtensionId';
export const CUSTOM_WA_INTEGRATION_ID = 'custom.waIntegrationId';
export const CUSTOM_WA_STT_MODEL = 'custom.waSTTModel';
export const CUSTOM_WA_TTS_VOICE = 'custom.waTTSVoice';

export const getProductProps = () => {
  if (isWxoEnabled) {
    return {
      productId: isAws ? ProductId.WXOAWS : ProductId.WXOIBM,
      productTitle: ProductTitle.WXO,
      environment: deployEnv,
      UT30: Ut30Code.WXO,
      productCode: isAws ? ProductCode.WXOAWS : ProductCode.WXOIBM,
      productCodeType: ProductCodeType,
      platformTitle: PlatformTitle.WXO,
      cloudEnvironment: isAws ? CloudEnvironment.AWS : CloudEnvironment.IBM,
      instanceId: 'unknown', // Adding to product props because required for every event otherwise event will get blocked
      instanceGuid: 'unknown' // Adding to product props because required for every event otherwise event will get blocked
    };
  } else {
    return {
      productId: ProductId.WXA,
      productTitle: ProductTitle.WXA,
      environment: deployEnv,
      UT30: Ut30Code.WXA,
      productCode: ProductCode.WXA,
      productCodeType: ProductCodeType,
      platformTitle: PlatformTitle.WXA,
      cloudEnvironment: CloudEnvironment.IBM,
      instanceId: 'unknown', // Adding to product props because required for every event otherwise event will get blocked
      instanceGuid: 'unknown' // Adding to product props because required for every event otherwise event will get blocked
    };
  }
};

const CUSTOM_PROP_PREFIX = 'custom.';
const OPERATORS = [
  'eq',
  'neq',
  'lt',
  'lte',
  'gt',
  'gte',
  'exists',
  'expression'
];

const asBool = value => (value ? true : false);
const asList = (values, prop) =>
  values ? `[${values.map(value => value[prop])}]` : '[]';
const checkTypeString = value => typeof value === 'string';
const checkIfNullOrUndefined = value => value === null || value === undefined;
const checkTypeObjectOrArray = value => typeof value === 'object';

const checkIfMultipleConditions = condition => {
  const groupsOperator = Object.keys(condition)[0];
  const firstBlock = condition[groupsOperator][0];
  return (
    (groupsOperator === 'and' || groupsOperator === 'or') &&
    firstBlock &&
    (Object.keys(firstBlock)[0] === 'and' ||
      Object.keys(firstBlock)[0] === 'or')
  );
};

let pageCounter = 0;

const addDisambiguation = (data, settings) => {
  const disambiguation = _.get(settings, 'disambiguation');
  if (disambiguation) {
    data['custom.skillDisambiguationEnabled'] = asBool(disambiguation.enabled);
    data['custom.skillDisambiguationMaxSuggestions'] =
      disambiguation.max_suggestions;
  }
};

const addAutocorrect = (data, settings) => {
  const autoCorrect = !!_.get(settings, 'spelling_auto_correct');
  data['custom.skillAutoCorrectionEnabled'] = autoCorrect;
};

const addSynonymCount = (data, options) => {
  let totalSynonyms = 0;
  if (options) {
    options.forEach(option => {
      totalSynonyms += _.get(option, 'synonyms.length', 0);
    });
  }
  data['custom.stepSynonymCount'] = totalSynonyms;
};

const addResponseType = (data, step) => {
  const customerResponse = _.get(step, 'question.entity');
  let responseType = null;

  if (customerResponse) {
    if (customerResponse === 'sys-number') {
      responseType = 'number';
    } else if (customerResponse === 'sys-time') {
      responseType = 'time';
    } else if (customerResponse === 'sys-date') {
      responseType = 'date';
    } else if (customerResponse === 'sys-percent') {
      responseType = 'percent';
    } else if (customerResponse === 'sys-currency') {
      responseType = 'currency';
    } else {
      responseType = 'options';
    }
  }
  data['custom.stepResponseType'] = responseType;
};

const addStepPosition = (data, orderedSteps, step) => {
  data['custom.stepPosition'] = orderedSteps.findIndex(
    s => s === _.get(step, 'step')
  );
};

export const getConditionCountHelper = (data, condition) => {
  const key = Object.keys(condition)[0];
  if (OPERATORS.includes(key)) {
    data['custom.stepConditionCount']++;
  } else if (key === 'not') {
    return getConditionCountHelper(data, condition[key]);
  } else if (key === 'and' || key === 'or') {
    const nestedConditions = condition[key]; // NOTE: nestedConditions will always be an array
    nestedConditions.forEach(condition =>
      getConditionCountHelper(data, condition)
    );
  }
};

export const addConditionCount = (data, condition) => {
  data['custom.stepConditionCount'] = 0;
  if (!_.isEmpty(condition)) {
    getConditionCountHelper(data, condition);
  }
};

export const addConditionGroupCount = (data, condition) => {
  let conditionGroupCount = 0;
  if (!_.isEmpty(condition)) {
    const key = Object.keys(condition);
    if (!_.isEmpty(condition[key])) {
      if (checkIfMultipleConditions(condition)) {
        conditionGroupCount += condition[key].length;
      } else {
        conditionGroupCount++; // if it's not multiple condition groups, then it's just 1 group
      }
    }
  }
  data['custom.stepConditionGroupCount'] = conditionGroupCount;
};

const addAllStepsWithConditionCount = (data, allSteps) => {
  let count = 0;
  _.forEach(allSteps, value => {
    if (value.condition) {
      count++;
    }
  });
  data['custom.actionStepsWithConditionsCount'] = count;
};

const addStepsWithInfo = (data, allSteps) => {
  let optionSteps = 0;
  let numbersSteps = 0;

  _.forEach(allSteps, value => {
    if (_.get(value, 'question.entity') === 'sys-number') {
      numbersSteps++;
    } else {
      const outputGeneric = _.get(value, 'output.generic', []);
      if (_.find(outputGeneric, { response_type: 'option' })) {
        optionSteps++;
      }
    }
  });

  data['custom.actionStepsThatCollectInformationCount'] =
    optionSteps + numbersSteps;
  data['custom.actionStepsThatCollectNumbersCount'] = numbersSteps;
  data['custom.actionStepsThatCollectOptionsCount'] = optionSteps;
};

const addContinueStepCount = (data, allSteps) => {
  let continueStepCount = 0;
  _.forEach(allSteps, value => {
    if (_.get(value, 'resolver.type') === 'continue') {
      continueStepCount++;
    }
  });
  data['custom.actionContinueToNextStepCount'] = continueStepCount;
};

const addStepVariablesCount = (data, allSteps) => {
  let setVariablesCount = 0;
  _.forEach(allSteps, value => {
    if (_.get(value, 'context.variables.length') > 0) {
      setVariablesCount++;
    }
  });
  data['custom.actionStepsThatSetVariablesCount'] = setVariablesCount;
};

const addStepsWithVariablesInResponseCount = (data, allSteps) => {
  let variableInResponseCount = 0;
  _.forEach(allSteps, value => {
    const response = _.get(value, 'output.generic');
    if (response) {
      const responseWithValues = _.find(response, 'values'); // object that contains values array
      if (responseWithValues && responseWithValues.values.length > 0) {
        const idx = _.findIndex(responseWithValues.values, 'text_expression');
        const varList = _.get(
          responseWithValues,
          `values[${idx}].text_expression.concat`
        );
        if (
          varList &&
          (_.find(varList, 'skill_variable') || _.find(varList, 'variable'))
        ) {
          variableInResponseCount++;
        }
      }
    }
  });
  data['custom.actionStepsWithVariableReferencedInResponseCount'] =
    variableInResponseCount;
};

const addStepsWithTitleCount = (data, allSteps) => {
  let count = 0;
  _.forEach(allSteps, value => {
    if (value.title) {
      count++;
    }
  });
  data['custom.actionStepsWithTitleCount'] = count;
};

const addWorkspaceSettings = (data, settings) => {
  const autoCorrect = _.get(
    settings,
    'human_agent_assist.spelling_auto_correct'
  );
  data['custom.skillAutoCorrectionEnabled'] = asBool(autoCorrect);

  const offTopic = _.get(settings, 'off_topic.enabled');
  data['custom.skillIrrelevanceDetection'] = offTopic
    ? 'enhanced'
    : 'keep existing';
};

export const getNewIaProps = isNewIa => {
  const data = {};
  data['custom.user.newIa'] = isNewIa;
  data.productVersion = 'classic';
  if (isNewIa) {
    data.productVersion = 'newia';
  }

  return data;
};

export const getAccountProps = account => {
  const data = {};
  if (account) {
    data.accountOwner = account.owner_iam_id;
    data.accountStatus = account.state;
    data.accountGuid = account.account_id;
    data.accountPlan = account.type;
  }
  return data;
};

export const getInstanceProps = (serviceInstanceId, region, plan, metering) => {
  const data = {};

  let planName = plan;

  // When running under WXO, we do not report the WXA name we received from
  // Store, but we "translate" the plan names to the corresponding WXO plan
  // names: "essentials", "standard" or "premium".
  if (isWxoEnabled) {
    if (plan === PlanName.PLUS) {
      planName = WxoPlanName.ESSENTIALS;
    } else if (plan === PlanName.ENTERPRISE) {
      planName = WxoPlanName.STANDARD;
    } else if (plan === PlanName.ENTERPRISE_DATA_ISOLATION) {
      planName = WxoPlanName.PREMIUM;
    }

    // For the WXO Agentic plans, we furthermore want to specify their metering
    // information
    if (metering) {
      planName = planName + '-agentic-' + metering;
    }
  }

  if (serviceInstanceId && plan && region) {
    data.region = region;
    data.instanceId = serviceInstanceId;
    data.instanceGuid = serviceInstanceId;
    data.productPlan = planName;
  }
  return data;
};

export const getEnvironmentProps = environment => {
  const data = {};
  if (environment) {
    data['custom.waEnvironmentId'] = environment.environment_id;
    data['custom.waEnvironmentName'] = environment.name;
  }
  return data;
};

export const getTargetEnvironmentProps = environment => {
  const data = {};
  if (environment) {
    data['custom.waTargetEnvironmentId'] = environment.environment_id;
    data['custom.waTargetEnvironmentName'] = environment.name;
  }
  return data;
};

export const getAccessControlProps = roles => {
  const data = {};
  if (roles) {
    data['custom.user.waInstanceRole'] = roles.instance;
  }
  return data;
};

export const getAssistantProps = (assistant, roles) => {
  const data = {};
  if (assistant) {
    data.agentId = assistant.assistant_id;
    data.agentType = 'Assistant';
    if (roles) {
      data['custom.user.assistantRole'] = roles.assistant;
    }
  }
  return data;
};

export const getIntegrationProps = integration => {
  const data = {};
  if (integration) {
    data['custom.waIntegrationId'] = integration.id;
    data['custom.waIntegrationType'] =
      channelTypeToChannelName[integration.type];
  }
  return data;
};

export const getCustomExtensionProps = customExtension => {
  const data = {};
  if (customExtension) {
    data['custom.customExtensionId'] = customExtension.catalog_item_id;
    data['custom.customExtensionType'] = customExtension.specification.type;
  }
  return data;
};

export const getSkillProps = (skill, roles) => {
  const data = {};
  if (skill) {
    const settings = _.get(skill, 'workspace.system_settings');
    const actionSkill = _.get(skill, 'type') === SkillType.ACTION;
    data['custom.waSkillId'] = skill.skill_id;
    data['custom.skillLanguage'] = skill.language;
    data['custom.skillType'] = actionSkill ? 'action' : 'dialog';
    data['custom.waSkillVersion'] = 'draft'; // TODO: Can this be anything else??
    data['custom.skillVersionCount'] = skill.snapshot_references
      ? skill.snapshot_references.length
      : 0;
    if (skill.assistant_references) {
      data['custom.skillLinkedAssistantCount'] =
        skill.assistant_references.length;
      data['custom.skillLinkedAssistantIds'] = asList(
        skill.assistant_references,
        'assistant_id'
      );
    }
    if (skill.counts) {
      data['custom.skillDialogNodeCount'] = skill.counts.dialog_nodes || 0;
      data['custom.skillIntentCount'] = skill.counts.intents || 0;
      data['custom.skillEntityCount'] = skill.counts.entities || 0;
      data['custom.skillActionCount'] = skill.counts.actions || 0;
    }

    if (settings) {
      addWorkspaceSettings(data, settings);
      addDisambiguation(data, settings);
      addAutocorrect(data, settings);
    }
    if (roles) {
      data['custom.user.skillRole'] = roles.skill;
    }
  }
  return data;
};

/*
 * NOTE: custom.stepValidationMessage is skipped for now
 * */
export const getStepProps = actionsStepData => {
  const {
    actionsStep,
    actionsStepOptions,
    actionsOrderedSteps,
    actionsStepValidation,
    assistant
  } = actionsStepData;

  const data = {};

  // The current form of the step id is only unique in the context of an assistant. To avoid duplications in Amplitude
  // when many assistants are loaded, prepend the assistant GUID. This will ensure the property is indeed unique.
  // If the current step is not null then prepend the assistant GUID. Otherwise set the id to null
  if (_.get(actionsStep, 'step', null)) {
    data['custom.stepId'] = assistant
      ? assistant.assistant_id + '-' + _.get(actionsStep, 'step', null)
      : _.get(actionsStep, 'step', null);
  } else {
    data['custom.stepId'] = null;
  }
  data['custom.stepOptionCount'] = actionsStepOptions
    ? actionsStepOptions.length
    : 0;
  data['custom.stepNumberOfTriesValue'] = actionsStepValidation.numberOfTurns;
  data['custom.stepVariablesSet'] =
    _.get(actionsStep, 'context.variables.length') > 0;
  addResponseType(data, actionsStep); // responseType
  addSynonymCount(data, actionsStepOptions); // synonymCount
  addStepPosition(data, actionsOrderedSteps, actionsStep); // stepPosition
  addConditionCount(data, _.get(actionsStep, 'condition', {})); // conditionCount
  addConditionGroupCount(data, _.get(actionsStep, 'condition', {})); // conditionGroupCount

  return data;
};

/*
 * NOTE:
 * custom.stepsUsingBoldCount
 * custom.stepsUsingItalicCount
 * custom.stepsUsingLinkCount
 *
 * are skipped for now
 * */
export const getActionProps = (
  currentAction,
  actionDetails,
  triggerExamples,
  autoSaveEnabled,
  allSteps,
  assistant,
  confirmation_enabled
) => {
  const data = {};

  // The current form of the action id is only unique in the context of an assistant. To avoid duplications in Amplitude
  // when many assistants are loaded, prepend the assistant GUID. This will ensure the property is indeed unique.
  // If the current action is not null then prepend the assistant GUID. Otherwise set the id to null
  if (_.get(currentAction, 'id', null)) {
    data['custom.actionId'] = assistant
      ? assistant.assistant_id + '-' + _.get(currentAction, 'id', null)
      : _.get(currentAction, 'id', null);
  } else {
    data['custom.actionId'] = null;
  }
  data['custom.actionCategory'] = currentAction
    ? currentAction.action === 'welcome' ||
      currentAction.action === 'anything_else'
      ? 'default'
      : 'custom'
    : null;
  data['custom.actionTotalStepCount'] = _.get(currentAction, 'steps.length', 0);
  data['custom.actionEndStepCount'] = actionDetails.numEndSteps;
  data['custom.actionRepromptStepCount'] = actionDetails.numReturnSteps;
  data['custom.actionUserExampleCount'] = triggerExamples.length;
  data['custom.actionAutoSaveEnabled'] = autoSaveEnabled;
  data['custom.confirmation_enabled'] = !confirmation_enabled; // this is generated before it gets saved into redux
  addAllStepsWithConditionCount(data, allSteps); // stepsWithConditionsCount
  addStepsWithInfo(data, allSteps); // stepsThatCollectInformationCount, stepsThatCollectNumberCount, stepsThatCollectOptionCount
  addContinueStepCount(data, allSteps); // continueToNextStepCount
  addStepVariablesCount(data, allSteps); // stepsThatSetVariablesCount
  addStepsWithVariablesInResponseCount(data, allSteps); // stepsWithVariableReferencedInResponseCount
  addStepsWithTitleCount(data, allSteps); //stepsWithTitlesCount

  return data;
};

export const getIdentifyProps = plan => {
  const data = {};
  data.pageChanges = ++pageCounter;
  if (plan) {
    data.instancePlan = plan;
  }
  return data;
};

// Ensure all event property values are strings and remove any
// that are not supported.
export const validateEventProps = (eventProps, supportedProps) => {
  if (eventProps) {
    // Remove any unsupported props or props with null or undefined values.
    // Convert all other props into strings.
    const supportedKeys = supportedProps ? Object.keys(supportedProps) : null;
    Object.entries(eventProps).forEach(([key, value]) => {
      if (
        !checkIfNullOrUndefined(value) &&
        (!supportedKeys || supportedKeys.includes(key))
      ) {
        if (!checkTypeString(value)) {
          eventProps[key] = JSON.stringify(value);
        }
      } else {
        delete eventProps[key];
      }
    });

    // Deal with the two keys that are not a string.
    if (eventProps.successFlag) {
      eventProps.successFlag = eventProps.successFlag.toLowerCase() === 'true';
    }
    if (eventProps.totalLength) {
      eventProps.totalLength = Number(eventProps.totalLength);
    }
  }
  return eventProps;
};

/**
 * Custom properties that will be allowed in a segment event.
 * Please make sure the property you want to add is not already here.
 */
export const customKeys = Object.freeze([
  // Classic
  'skillType',

  // New IA
  'skillId',
  'waEnvironmentId',
  'waEnvironmentName',

  // Onboarding wizard user preferences dropdowns
  'wizardChannel',
  'wizardIndustry',
  'wizardRole',
  'wizardUseCase',

  // Home wizard
  'taskCompleted',
  'taskId',

  // Publish and revert will send current and target snapshot version
  'waCurrentVersion',
  'waTargetEnvironmentId',
  'waTargetEnvironmentIds',
  'waTargetVersion',

  // Integrations/channels
  'waIntegrationId',
  'waIntegrationType',
  'waPhoneProvider',
  'waSTTModel',
  'waTTSVoice',
  'waWebchatSuggestionsIsOn', //only in classic when toggling suggestions

  // Custom extensions
  'customExtensionId',
  'customExtensionType', // specification type
  'waFileName', // openapi spec waFileName

  // Settings, comma separated
  'changedFields',

  //Modal - Extend trial
  'daysLeft',
  'isIBMUser',

  //Multistep modal - Switch to Classic IA
  'switcherOption',
  'switcherReason',

  //Actions
  //-------
  'actionId',
  'actionTitle',
  //Boost Word count
  'waBoostWordsCount',
  //Global Variable
  'variableScope',
  //Assignee Variable Type.
  'variableType',
  //Step reorder
  'newStepPosition',
  'previousStepPosition',
  //ID of step being accessed
  'stepId',
  //change step type dropdown selected
  'stepType',
  //change step type item selected
  'waNewValue',
  'waPreviousValue',
  //Response Type item selected
  'responseAdded',
  'waResponseType',
  //Preview
  'waLanguage',
  'messageLengthOfInput',
  'messageSkillIsTraining',
  //Trigger phrases
  'waTriggerPhraseCount',
  //Template Id collected from template catalog page
  'templateId',
  //Template category selected on template catalog page
  'templateCategory',

  //Copy Action to an assistant
  'waTargetAssistant',

  //Delete step
  'stepHasContent', // whether the step has title, condition, etc
  'stepType', // default next step is continue

  //Dialog
  'waExamples', //intents user example
  'waJumpToAnd',
  'waJumpToNodeName',
  'waNodeGeneration',
  'waSourceType', //intents user example
  'waValueCount', //entityValue

  //Try It - Webchat preview
  'messageCategory',
  'messageConditionType',
  'messageDialogError',
  'messageEntityCount',
  'messageIsPrebuilt',
  'messagePromptType',
  'messageResponseTypes',
  'messageResultCode',
  'messageTopIntentConfidence',

  // Errors
  'waErrorSummary',
  'waErrorStack',

  //Search
  'skillId'
]);

/**
 * Ensure all custom properties have keys that start with 'custom.'.
 * Currently a warning is presented for any unsupported custom property.
 * Eventually invalid properties will be automatically removed.
 */
export const validateCustomProps = _customProps => {
  if (_customProps) {
    const customProps = { ..._customProps };

    Object.entries(customProps).forEach(([key, value]) => {
      if (!customKeys.includes(key)) {
        console.warn(
          `Ignoring custom property: "${key}" from segment event.\nAllowed custom properties: ${customKeys}`
        );
      }
      if (!key.startsWith(CUSTOM_PROP_PREFIX)) {
        const updatedKey = CUSTOM_PROP_PREFIX + key;
        customProps[updatedKey] = value;
        delete customProps[key];
        key = updatedKey;
      }
      if (checkIfNullOrUndefined(value) || checkTypeObjectOrArray(value)) {
        customProps[key] = '' + JSON.stringify(value);
      }
    });
    return customProps;
  }
  return _customProps;
};
